import React, { FC, useState } from 'react'

import { useForm } from 'react-hook-form'

import { TextField } from 'src/components/atoms'
import { EditModeIconsSet } from 'src/components/molecules/inputs/EditModeIconsSet'
import { DeleteAllowedIpModal } from 'src/components/organisms'

import { yup, BaseYup, yupResolver } from 'src/customYup'
import {
  useError,
  useGetIpRestriction,
  useGlobalLoading,
  useIpRestriction,
  useToast,
} from 'src/hooks'
import { AllowedIpEntity, UpsertAllowedIpFormEntity } from 'src/types'

const schema: yup.SchemaOf<UpsertAllowedIpFormEntity> = BaseYup.object({
  ip: BaseYup.string()
    .required()
    .matches(
      /^([01]?\d{1,2}|2[0-4]\d|25[0-5])\.([01]?\d{1,2}|2[0-4]\d|25[0-5])\.([01]?\d{1,2}|2[0-4]\d|25[0-5])\.([01]?\d{1,2}|2[0-4]\d|25[0-5])$/g,
      'IPアドレスを入力ください'
    ),
  description: BaseYup.string().required(),
})

type Props = {
  allowedIp?: AllowedIpEntity | null
  afterCrud: () => void
  deleteNewItem: () => void
  onCancel?: () => void
  className?: string
}

export const UpsertAllowedIp: FC<Props> = ({
  allowedIp,
  afterCrud,
  deleteNewItem,
  onCancel,
  className,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<UpsertAllowedIpFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: {
      ip: allowedIp?.ip,
      description: allowedIp?.description,
    },
  })

  const { showGlobalLoading, hideGlobalLoading } = useGlobalLoading()
  const { createAllowedIp, updateAllowedIp } = useIpRestriction()
  const { toErrorEntities } = useError()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { ipRestriction } = useGetIpRestriction()

  const onSubmit = async (formEntity: UpsertAllowedIpFormEntity) => {
    showGlobalLoading()

    try {
      if (allowedIp) {
        await updateAllowedIp({ allowedIpId: allowedIp.id, formEntity })
        displayStandardTextToast('IPアドレスを更新しました')
      } else {
        await createAllowedIp({ formEntity })
        displayStandardTextToast('IPアドレスを追加しました')
      }
      afterCrud()
    } catch (error) {
      const errors = toErrorEntities(error)
      console.log('errors: ', errors)

      const errorMessage = allowedIp
        ? 'IPアドレスの更新に失敗しました'
        : 'IPアドレスの追加に失敗しました'
      displayErrorTextToast(errorMessage)
    } finally {
      hideGlobalLoading()
    }
  }

  const handleOnClickDelete = () => {
    if (!allowedIp) {
      deleteNewItem()
      return
    }
    setShowDeleteModal(true)
  }

  const handleAfterDelete = () => {
    afterCrud()
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-flex tw-gap-r2">
          <div className="tw-flex tw-gap-r2 tw-w-full">
            <TextField
              placeholder="000.000.000.000"
              errorMessage={formErrors.ip?.message}
              {...register('ip')}
              className="tw-w-[11rem] tw-shrink-0"
            />
            <TextField
              placeholder="概要"
              errorMessage={formErrors.description?.message}
              {...register('description')}
              className="tw-w-full"
            />
          </div>
          <EditModeIconsSet
            disabledCancel={!allowedIp}
            disabledSubmit={!watch('ip') || !watch('description')}
            onClickDelete={handleOnClickDelete}
            onClickCancel={() => allowedIp && onCancel && onCancel()}
            className="tw-shrink-0 tw-flex tw-justify-center tw-w-[8.58rem]"
          />
        </div>
      </form>

      {allowedIp && ipRestriction && (
        <DeleteAllowedIpModal
          allowedIp={allowedIp}
          isOpen={showDeleteModal}
          isLast={ipRestriction.allowedIps?.length === 1}
          onClose={() => setShowDeleteModal(false)}
          afterDelete={handleAfterDelete}
        />
      )}
    </div>
  )
}
